export function HowToSellApartmentBuilding() {
  return (
    <section className="py-20 bg-gray-50" id="how-to-sell">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            How Do I Sell My Apartment Building?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Selling an apartment building can be complex, but understanding your options helps you make the best decision. 
            Here's everything you need to know about selling your multifamily investment property.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/6597bce14f016e4049d50ed6dda30e75.jpg"
              alt="Real estate consultation for apartment building sale"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Traditional vs. Direct Sale Options</h3>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Traditional Real Estate Agent</h4>
                <p className="text-gray-600 mb-3">
                  Working with a commercial real estate agent typically takes 6-12 months and involves extensive marketing, 
                  showings, and negotiations. You'll pay 6-10% in commissions and closing costs.
                </p>
                <ul className="text-gray-600 space-y-1">
                  <li>• Longer timeline (6-12 months)</li>
                  <li>• Higher fees (6-10% total costs)</li>
                  <li>• Extensive marketing required</li>
                  <li>• Multiple showings and negotiations</li>
                </ul>
              </div>
              
              <div className="bg-orange-50 p-6 rounded-lg border-2 border-orange-200">
                <h4 className="text-xl font-semibold text-orange-800 mb-3">Direct Cash Sale</h4>
                <p className="text-gray-700 mb-3">
                  Sell directly to an investor for a faster, simpler process. Close in 7-30 days with no commissions, 
                  repairs, or lengthy marketing periods.
                </p>
                <ul className="text-gray-700 space-y-1">
                  <li>• Quick timeline (7-30 days)</li>
                  <li>• No commissions or fees</li>
                  <li>• Sell as-is condition</li>
                  <li>• Guaranteed closing</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Step-by-Step Guide to Selling Your Apartment Building
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Property Evaluation</h4>
              <p className="text-gray-600">
                Assess your building's condition, rental income, expenses, and current market value to determine your asking price.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Financial Documentation</h4>
              <p className="text-gray-600">
                Gather rent rolls, operating statements, tax records, and maintenance logs to show potential buyers.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Marketing Strategy</h4>
              <p className="text-gray-600">
                Choose between traditional listing with an agent or direct sale to investors for faster results.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">4</span>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Close the Deal</h4>
              <p className="text-gray-600">
                Review offers, negotiate terms, and coordinate with your attorney and accountant for a smooth closing.
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
              <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-4">Maximize Your Sale Price</h4>
            <p className="text-gray-600 mb-4">
              Understanding your property's true value is crucial. Consider factors like location, condition, rental income, 
              and market comparables when setting your price.
            </p>
            <ul className="text-gray-600 space-y-2">
              <li>• Get multiple property evaluations</li>
              <li>• Review recent comparable sales</li>
              <li>• Consider income capitalization approach</li>
              <li>• Factor in deferred maintenance costs</li>
            </ul>
          </div>
          
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
              <i className="ri-time-line text-2xl text-blue-600"></i>
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-4">Timeline Considerations</h4>
            <p className="text-gray-600 mb-4">
              The timeline for selling your apartment building varies significantly based on your chosen method and local market conditions.
            </p>
            <ul className="text-gray-600 space-y-2">
              <li>• Traditional sale: 6-12 months</li>
              <li>• Cash sale to investor: 7-30 days</li>
              <li>• Auction sale: 30-60 days</li>
              <li>• Owner financing: 30-90 days</li>
            </ul>
          </div>
          
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-6">
              <i className="ri-alert-line text-2xl text-red-600"></i>
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-4">Common Mistakes to Avoid</h4>
            <p className="text-gray-600 mb-4">
              Avoid these costly mistakes that can delay your sale or reduce your final selling price.
            </p>
            <ul className="text-gray-600 space-y-2">
              <li>• Overpricing your property</li>
              <li>• Inadequate financial documentation</li>
              <li>• Neglecting property maintenance</li>
              <li>• Poor tenant communication</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 bg-orange-50 rounded-xl p-12 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to Sell Your Apartment Building?
          </h3>
          <p className="text-lg text-gray-600 mb-8">
            Get a free, no-obligation cash offer for your apartment building. We buy properties in any condition 
            and can close in as little as 7 days.
          </p>
          <button className="bg-orange-500 hover:bg-orange-600 text-white px-12 py-4 rounded-lg font-semibold text-lg whitespace-nowrap">
            Get Your Free Cash Offer
          </button>
        </div>
      </div>
    </section>
  );
}